import { TipoDomandaDTO, useGetFrontTipiUtente } from '@framework/api-client';
import { useCurrentUser } from '@framework/auth';
import { StyledHtmlText } from '@framework/engine';
import { SubmissionLogic } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { VerticalSpace } from '@framework/ui';
import { Button } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import DOMPurify from 'dompurify';
import { useSelectedLegalEntity } from '../../legal-entity/useSelectedLegalEntity';
import { useCreateSubmissionAndLinks } from '../hooks/useCreateSubmissionAndLinks';
import { SubmissionAccreditationLogic } from './SubmissionAccreditationLogic';

export interface ICreateRequestButtonProps {
  inquiry?: TipoDomandaDTO;
}

function useIsBlockedByOB9(inquiry?: TipoDomandaDTO) {
  const user = useCurrentUser();

  // Una domanda in collaudo è sempre compilabile
  if (SubmissionLogic.isInTesting(inquiry)) {
    return false;
  }

  // Controllo dedicato per domanda OB9_Progressione_Operatori_Assistenti
  if (
    user?.utenteLoggato?.datiCustomAggiuntivi?.profilo?.areaProfilo !== 'OP' &&
    inquiry?.codice?.trim() === 'OB9_Progressione_Operatori_Assistenti'
  ) {
    return true;
  }

  return false;
}

/**
 * Bottone della lista delle domande che permette di creare una nuova Richiesta.
 * Una volta creata fa in automatico il redirect alla pagina di compilazione.
 */
export function CreateSubmissionButton(props: ICreateRequestButtonProps) {
  const { inquiry } = props;
  const { t } = useTranslation();
  const isUniqueRequest = inquiry?.flRichiestaUnivoca;

  // Temporaneo
  const isBlockedByOB9 = useIsBlockedByOB9(inquiry);

  const { selectedLegalEntity } = useSelectedLegalEntity();
  const { data: userKinds } = useGetFrontTipiUtente();

  const isBlockedByAccreditation =
    SubmissionAccreditationLogic.isBlockedByAccreditation(
      inquiry,
      userKinds,
      selectedLegalEntity,
    );

  const { handleClick, loading } = useCreateSubmissionAndLinks(inquiry?.id);

  const readOnlyMessage = inquiry?.nowInFasciaOrariaSolaLettura
    ? inquiry?.messaggioAvvisoSolaLettura ??
      t(
        'domanda.bottone-crea-richiesta.sola-lettura',
        'Attualmente non è possibile compilare questa domanda. Si prega di riprovare in un altro orario.',
      )
    : undefined;

  if (isBlockedByOB9) return null;

  if (readOnlyMessage) {
    return (
      <Paragraph>
        <StyledHtmlText
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(String(readOnlyMessage)),
          }}
        />
      </Paragraph>
    );
  }

  return (
    <>
      <Button
        type="primary"
        block
        size="large"
        onClick={() => {
          handleClick();
        }}
        loading={loading}
        disabled={
          !SubmissionLogic.canCreateSubmission(inquiry) ||
          isBlockedByAccreditation
        }
      >
        {inquiry?.copertina?.pulsanteAccesso ??
          t(
            'domanda.bottone-crea-richiesta.vai-alla-compilazione',
            'Vai alla compilazione',
          )}
      </Button>
      {isUniqueRequest && (
        <>
          <VerticalSpace size={8} />
          <Paragraph>
            {t(
              'domanda.bottone-compilare-una-richiesta-per-titolare',
              'È possibile compilare una sola richiesta per titolare per questa domanda',
            )}
          </Paragraph>
        </>
      )}
    </>
  );
}
